/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Edit,
  Eye,
  Download,
  FileText,
  CheckCircle,
  Clock,
  Calendar,
  ArrowRight,
  ChevronDown,
  X,
  ChevronRight,
} from "lucide-react";
import Pagination from "../../../components/Pagination";

// --- Mock Data ---

const MOCK_TEMPLATE = {
  id: "TMP-1",
  name: "Standard Caregiver Contract",
  description:
    "This agreement outlines the terms and conditions for caregivers providing standard home care services, including duties, compensation, and confidentiality.",
  isAutomatic: true,
  trigger: "Caregiver Assignment Accepted",
  sender: "Tenant Admin",
  recipient: "Service Provider (Caregiver)",
  createdDate: "Oct 10, 2024",
  status: "Active",
  content: `AGREEMENT FOR CAREGIVER SERVICES

This Agreement is made on this day between the Service Provider and the Service Buyer...

1. SERVICES PROVIDED
The Caregiver agrees to provide the following services...

2. COMPENSATION
Payment shall be made on a weekly/monthly basis...

3. CONFIDENTIALITY
The Caregiver agrees to keep all client information confidential...`,
};

const MOCK_COMPLETED = Array.from({ length: 8 }, (_, i) => ({
  id: `AGR-${200 + i}`,
  agreementNumber: `#AGR-2024-${800 + i}`,
  provider: ["Bethelhem Alemu", "Tigist Bekele", "Hana Girma"][i % 3],
  buyer: ["Yonatan Solomon", "Sarah Smith", "Michael Chen"][i % 3],
  signedDate: "Oct 24, 2024",
  status: i % 4 === 0 ? "Pending" : "Signed",
}));

const AgreementTemplateDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("details");
  const [status, setStatus] = useState(MOCK_TEMPLATE.status);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState({
    title: MOCK_TEMPLATE.name,
    content: MOCK_TEMPLATE.content,
    isSigned: false,
  });

  // Pagination for completed list
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const totalPages = Math.ceil(MOCK_COMPLETED.length / itemsPerPage);
  const paginatedCompleted = MOCK_COMPLETED.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const getStatusColor = (s) => {
    switch (s) {
      case "Active":
      case "Signed":
        return "text-primary bg-primary/10 dark:bg-primary/20 dark:text-primary";
      case "Inactive":
      case "Expired":
        return "text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400";
      case "Draft":
        return "text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-dark-muted";
      case "Pending":
        return "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const handlePreviewTemplate = () => {
    setPreviewContent({
      title: MOCK_TEMPLATE.name,
      content: MOCK_TEMPLATE.content,
      isSigned: false,
    });
    setShowPreview(true);
  };

  const handleViewContract = (contract) => {
    // Simulate fetching specific contract content with filled data
    const filledContent = MOCK_TEMPLATE.content
      .replace("Service Provider", `Service Provider (${contract.provider})`)
      .replace("Service Buyer", `Service Buyer (${contract.buyer})`);

    setPreviewContent({
      title: `${MOCK_TEMPLATE.name} - ${contract.agreementNumber}`,
      content: filledContent,
      isSigned: contract.status === "Signed",
    });
    setShowPreview(true);
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 pb-12 bg-background dark:bg-dark-background min-h-screen p-4">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-dark-muted mb-6">
        <Link to="/agreements" className="hover:text-primary transition-colors">
          Agreement templates
        </Link>
        <ChevronRight size={14} />
        <span className="text-text dark:text-dark-text font-medium">
          {MOCK_TEMPLATE.name}
        </span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-primary/10 dark:bg-primary/20 text-primary rounded-lg">
              <FileText size={24} />
            </div>
            <h1 className="text-3xl font-bold text-text dark:text-dark-text">
              {MOCK_TEMPLATE.name}
            </h1>
          </div>
          <p className="text-gray-500 dark:text-dark-muted ml-11">
            Template ID: {id}
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handlePreviewTemplate}
            className="flex items-center gap-2 px-4 py-2.5 bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border text-text dark:text-dark-muted rounded-xl font-medium hover:bg-accent dark:hover:bg-gray-700 transition-colors shadow-sm"
          >
            <Eye size={18} /> Preview agreement
          </button>
          <button
            onClick={() => navigate(`/agreements/edit/${id}`)}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold shadow-lg shadow-primary/20 dark:shadow-none transition-all"
          >
            <Edit size={18} /> Edit
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-8 border-b border-gray-200 dark:border-dark-border mb-8">
        <button
          onClick={() => setActiveTab("details")}
          className={`pb-4 text-sm font-bold transition-all relative ${
            activeTab === "details"
              ? "text-primary dark:text-primary"
              : "text-gray-500 dark:text-dark-muted hover:text-text dark:hover:text-dark-text"
          }`}
        >
          Details
          {activeTab === "details" && (
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary dark:bg-primary rounded-t-full"></span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("completed")}
          className={`pb-4 text-sm font-bold transition-all relative ${
            activeTab === "completed"
              ? "text-primary dark:text-primary"
              : "text-gray-500 dark:text-dark-muted hover:text-text dark:hover:text-dark-text"
          }`}
        >
          Completed agreements
          {activeTab === "completed" && (
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary dark:bg-primary rounded-t-full"></span>
          )}
        </button>
      </div>

      {/* Content Area */}
      <div className="bg-card dark:bg-dark-surface rounded-[2rem] shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden min-h-[500px]">
        {activeTab === "details" && (
          <div className="p-8 md:p-10 animate-in fade-in zoom-in-95 duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
              <div className="md:col-span-2">
                <h3 className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider mb-2">
                  Description
                </h3>
                <p className="text-text dark:text-dark-muted leading-relaxed max-w-4xl">
                  {MOCK_TEMPLATE.description}
                </p>
              </div>

              <div>
                <h3 className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider mb-2">
                  Automatic Generation
                </h3>
                <div className="flex items-center gap-2">
                  {MOCK_TEMPLATE.isAutomatic ? (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-sm font-bold">
                      <Clock size={14} /> Yes
                    </span>
                  ) : (
                    <span className="text-text dark:text-dark-text font-medium">
                      No
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                  System automatically generates this agreement when trigger is
                  met.
                </p>
              </div>

              <div>
                <h3 className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider mb-2">
                  Trigger Event
                </h3>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded text-purple-600 dark:text-purple-400">
                    <CheckCircle size={16} />
                  </div>
                  <span className="font-medium text-text dark:text-dark-text">
                    {MOCK_TEMPLATE.trigger}
                  </span>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider mb-2">
                  Recipient Flow
                </h3>
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-bold text-text dark:text-dark-text bg-accent dark:bg-gray-700 px-3 py-1.5 rounded-lg">
                    {MOCK_TEMPLATE.sender}
                  </span>
                  <ArrowRight
                    size={16}
                    className="text-gray-400 dark:text-dark-muted"
                  />
                  <span className="font-bold text-text dark:text-dark-text bg-accent dark:bg-gray-700 px-3 py-1.5 rounded-lg">
                    {MOCK_TEMPLATE.recipient}
                  </span>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider mb-2">
                  Date Created
                </h3>
                <div className="flex items-center gap-2 text-text dark:text-dark-muted">
                  <Calendar
                    size={18}
                    className="text-gray-400 dark:text-dark-muted"
                  />
                  <span className="font-medium">
                    {MOCK_TEMPLATE.createdDate}
                  </span>
                </div>
              </div>

              <div className="md:col-span-2 pt-6 border-t border-gray-100 dark:border-dark-border">
                <h3 className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider mb-3">
                  Status Configuration
                </h3>
                <div className="relative w-full max-w-xs">
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full appearance-none pl-4 pr-10 py-3 rounded-xl bg-accent dark:bg-gray-900 border border-gray-200 dark:border-dark-border font-bold text-text dark:text-dark-text focus:ring-2 focus:ring-primary outline-none cursor-pointer"
                  >
                    <option>Active</option>
                    <option>Inactive</option>
                    <option>Draft</option>
                  </select>
                  <ChevronDown
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-dark-muted pointer-events-none"
                    size={16}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-dark-muted mt-2">
                  Changing status to &quot;Inactive&quot; will stop automatic
                  generation.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === "completed" && (
          <div className="flex flex-col h-full animate-in fade-in zoom-in-95 duration-300">
            <div className="overflow-x-auto flex-1">
              <table className="w-full text-sm text-left">
                <thead className="bg-accent dark:bg-gray-900/50 text-xs text-gray-500 dark:text-dark-muted uppercase font-semibold border-b border-gray-100 dark:border-dark-border">
                  <tr>
                    <th className="px-8 py-5">Agreement #</th>
                    <th className="px-8 py-5">Service Provider</th>
                    <th className="px-8 py-5">Service Buyer</th>
                    <th className="px-8 py-5">Date Signed</th>
                    <th className="px-8 py-5 text-center">Status</th>
                    <th className="px-8 py-5 text-center">Contract</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {paginatedCompleted.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-accent dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="px-8 py-4 font-mono text-gray-500 dark:text-dark-muted">
                        {row.agreementNumber}
                      </td>
                      <td className="px-8 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-700 dark:text-purple-400 font-bold text-xs">
                            {row.provider.substring(0, 2)}
                          </div>
                          <span className="font-medium text-text dark:text-dark-text">
                            {row.provider}
                          </span>
                        </div>
                      </td>
                      <td className="px-8 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-700 dark:text-blue-400 font-bold text-xs">
                            {row.buyer.substring(0, 2)}
                          </div>
                          <span className="font-medium text-text dark:text-dark-text">
                            {row.buyer}
                          </span>
                        </div>
                      </td>
                      <td className="px-8 py-4 text-gray-500 dark:text-dark-muted">
                        {row.signedDate}
                      </td>
                      <td className="px-8 py-4 text-center">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(
                            row.status,
                          )}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="px-8 py-4 text-center">
                        <button
                          onClick={() => handleViewContract(row)}
                          className="text-primary hover:text-primary/90 dark:hover:text-primary font-medium text-xs flex items-center justify-center gap-1 mx-auto hover:underline"
                        >
                          <Eye size={14} /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border-t border-gray-100 dark:border-dark-border bg-accent/50 dark:bg-gray-800/30 p-2">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-3xl h-[85vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-100 dark:border-dark-border flex justify-between items-center bg-accent/50 dark:bg-gray-800/50 rounded-t-2xl">
              <h3 className="text-lg font-bold text-text dark:text-dark-text flex items-center gap-2">
                <FileText size={20} className="text-primary" />{" "}
                {previewContent.isSigned
                  ? "Signed Contract"
                  : "Agreement Preview"}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  className="p-2 hover:bg-accent dark:hover:bg-gray-700 rounded-full text-gray-500 dark:text-dark-muted transition-colors"
                  title="Download PDF"
                >
                  <Download size={20} />
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-accent dark:hover:bg-gray-700 rounded-full text-gray-500 dark:text-dark-muted transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-8 md:p-12 bg-accent dark:bg-black/20 custom-scrollbar">
              <div className="bg-card dark:bg-dark-surface p-8 md:p-12 shadow-sm border border-gray-200 dark:border-dark-border min-h-full mx-auto max-w-2xl relative">
                {previewContent.isSigned && (
                  <div className="absolute top-10 right-10 border-4 border-primary text-primary rounded-full w-24 h-24 flex items-center justify-center opacity-30 -rotate-12 pointer-events-none">
                    <span className="font-black text-xl uppercase">SIGNED</span>
                  </div>
                )}

                <h1 className="text-2xl font-bold text-center mb-8 uppercase tracking-widest border-b-2 border-black dark:border-white pb-4">
                  {previewContent.title}
                </h1>

                <div className="whitespace-pre-wrap font-serif leading-loose text-text dark:text-dark-muted text-sm">
                  {previewContent.content}
                </div>

                <div className="mt-16 grid grid-cols-2 gap-12 pt-8">
                  <div>
                    <div className="h-px bg-black dark:bg-gray-500 mb-2"></div>
                    <p className="text-xs font-bold uppercase">
                      Service Provider Signature
                    </p>
                    {previewContent.isSigned && (
                      <p className="font-script text-lg text-primary mt-[-2.5rem] ml-4 -rotate-3">
                        Signed Digitally
                      </p>
                    )}
                  </div>
                  <div>
                    <div className="h-px bg-black dark:bg-gray-500 mb-2"></div>
                    <p className="text-xs font-bold uppercase">
                      Service Buyer Signature
                    </p>
                    {previewContent.isSigned && (
                      <p className="font-script text-lg text-primary mt-[-2.5rem] ml-4 -rotate-3">
                        Signed Digitally
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgreementTemplateDetails;
